// pages/ResetPasswordPage.tsx
import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "../api/axiosInstance";
import { API_ENDPOINTS } from "../constants/apiEndpoints";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const uid = searchParams.get("uid") ?? "";
  const token = searchParams.get("token") ?? "";

  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!uid || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-teal-50 px-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
          <p className="text-red-500 text-sm">유효하지 않은 비밀번호 재설정 링크입니다.</p>
          <Link to="/forgot-password" className="block mt-4 text-sm font-semibold text-[#2aa7aa] hover:underline">
            다시 요청하기
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPw !== newPw2) {
      setError("비밀번호 확인이 일치하지 않습니다.");
      return;
    }
    setLoading(true);
    try {
      await api.post(API_ENDPOINTS.AUTH.PASSWORD_RESET_CONFIRM, {
        uid,
        token,
        new_password: newPw,
        new_password2: newPw2,
      });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err: any) {
      const data = err?.response?.data;
      const msg =
        data?.token?.[0] ||
        data?.uid?.[0] ||
        data?.new_password2?.[0] ||
        data?.new_password?.[0] ||
        data?.detail ||
        data?.non_field_errors?.[0] ||
        "비밀번호 재설정에 실패했습니다.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-teal-50 px-4">
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-xl p-8">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold text-slate-900">새 비밀번호 설정</h1>
          <p className="mt-1 text-sm text-slate-500">새로운 비밀번호를 입력하세요.</p>
        </div>

        {success ? (
          <div className="text-center space-y-3">
            <div className="text-4xl">✅</div>
            <p className="text-sm text-slate-700">비밀번호가 재설정되었습니다.<br />로그인 페이지로 이동합니다.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">새 비밀번호</label>
              <input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="8자 이상"
                required
                minLength={8}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">새 비밀번호 확인</label>
              <input
                type="password"
                value={newPw2}
                onChange={(e) => setNewPw2(e.target.value)}
                required
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-teal-500 py-2.5 text-sm font-semibold text-white hover:bg-teal-600 disabled:opacity-50 transition-colors"
            >
              {loading ? "설정 중..." : "비밀번호 재설정"}
            </button>

            <Link to="/login" className="block text-center text-sm text-slate-500 hover:underline pt-1">
              ← 로그인으로 돌아가기
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
